/**
 * Serbest kalemler (28.10.2026, Ürün Sahibi kararı): satışçı özel tatil paketine kendi kalemini ekler (şoförlü VIP araç
 * tahsisi, VIP safari, vize hizmeti gibi). Ad, açıklama (düz metin), tutar, para birimi, hesap (sabit, kişi başı, gün başı,
 * saat başı) ve adet girilir; kalem TÜM seçeneklere ya da yalnız bir otel seçeneğine eklenir. Toplam her kalemde görünür.
 * Mevcut tasarım sistemi sınıfları, ham tarih alanı yok.
 */
import { useId } from 'react';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { Ikon } from './Ikon';
import { BIRIM_ETIKETI, KALEM_ACIKLAMA_AZAMI, KALEM_AZAMI, KALEM_MIKTAR_AZAMI, birimMiktari, kalemDayanagi, paraYaz, type KalemDegeri } from './mantik';
import type { KalemBirimi, ParaBirimi, SerbestKalem } from './tipler';

const BIRIMLER: readonly KalemBirimi[] = ['SABIT', 'KISI_BASI', 'GUN_BASI', 'SAAT_BASI'];
const PARALAR: readonly ParaBirimi[] = ['USD', 'AED', 'EUR'];

export interface KalemSecenegi {
  readonly anahtar: string;
  readonly ad: string;
}

export function SerbestKalemAlani({
  kalemler,
  degerler,
  secenekler,
  hedefPara,
  kisi,
  gece,
  devreDisi,
  degistir,
  ekle,
  kaldir,
}: {
  kalemler: readonly SerbestKalem[];
  degerler: Readonly<Record<string, KalemDegeri>>;
  secenekler: readonly KalemSecenegi[];
  hedefPara: ParaBirimi | null;
  kisi: number;
  gece: number | null;
  devreDisi: boolean;
  degistir: (id: string, yama: Partial<SerbestKalem>) => void;
  ekle: () => void;
  kaldir: (id: string) => void;
}) {
  const kimlik = useId();
  return (
    <section className="ht-kart" aria-labelledby={`${kimlik}-baslik`} data-testid="ht-kalemler">
      <div className="ht-kart-ust">
        <h2 id={`${kimlik}-baslik`}>{M.kalem.baslik}</h2>
        <span className="ht-sayi-et" data-testid="ht-kalem-sayi">{kalemler.length} / {KALEM_AZAMI}</span>
      </div>
      <p className="ht-ipucu">{M.kalem.ipucu}</p>
      {devreDisi ? <p className="ht-ipucu">{M.kalem.salt}</p> : null}
      {kalemler.length === 0 ? <p className="ht-ipucu" data-testid="ht-kalem-bos">{M.kalem.bos}</p> : null}
      <div className="ht-kalemler">
        {kalemler.map((k, i) => {
          const d = degerler[k.id];
          const kimlikK = `${kimlik}-${k.id}`;
          const kapsamDeger = k.kapsam === 'TUM' ? 'TUM' : (k.secenek ?? 'YOK');
          const secenekVar = k.kapsam === 'SECENEK' && k.secenek !== null && secenekler.some((s) => s.anahtar === k.secenek);
          return (
            <fieldset key={k.id} className="ht-kalem" data-testid={`ht-kalem-${i}`} disabled={devreDisi}>
              <legend>{M.kalem.baslikN(i + 1)}</legend>
              <div className="ht-kalem-izgara">
                <div className="ht-alan ht-kalem-genis">
                  <label htmlFor={`${kimlikK}-ad`}>{M.kalem.ad}</label>
                  <input id={`${kimlikK}-ad`} value={k.ad} maxLength={120} placeholder={M.kalem.adIpucu} autoComplete="off" aria-invalid={d?.hata === 'AD' ? 'true' : undefined} data-testid={`ht-kalem-ad-${i}`} onChange={(e) => degistir(k.id, { ad: e.target.value })} />
                </div>
                <div className="ht-alan ht-kalem-genis">
                  <label htmlFor={`${kimlikK}-aciklama`}>{M.kalem.aciklama}</label>
                  <textarea id={`${kimlikK}-aciklama`} rows={3} maxLength={KALEM_ACIKLAMA_AZAMI} value={k.aciklama} aria-invalid={d?.hata === 'ACIKLAMA' ? 'true' : undefined} data-testid={`ht-kalem-aciklama-${i}`} onChange={(e) => degistir(k.id, { aciklama: e.target.value })} />
                  <span className="ht-ipucu">{M.kalem.aciklamaSayac(k.aciklama.length, KALEM_ACIKLAMA_AZAMI)}</span>
                </div>
                <div className="ht-alan">
                  <label htmlFor={`${kimlikK}-tutar`}>{M.kalem.tutar}</label>
                  <input id={`${kimlikK}-tutar`} value={k.tutar} inputMode="decimal" placeholder={M.kalem.tutarIpucu} autoComplete="off" aria-invalid={d?.hata === 'TUTAR' ? 'true' : undefined} data-testid={`ht-kalem-tutar-${i}`} onChange={(e) => degistir(k.id, { tutar: e.target.value })} />
                </div>
                <div className="ht-alan">
                  <label htmlFor={`${kimlikK}-para`}>{M.kalem.para}</label>
                  <select id={`${kimlikK}-para`} value={k.paraBirimi} data-testid={`ht-kalem-para-${i}`} onChange={(e) => degistir(k.id, { paraBirimi: e.target.value as ParaBirimi })}>
                    {PARALAR.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="ht-alan">
                  <label htmlFor={`${kimlikK}-birim`}>{M.kalem.birim}</label>
                  <select id={`${kimlikK}-birim`} value={k.birim} data-testid={`ht-kalem-birim-${i}`} onChange={(e) => { const b = e.target.value as KalemBirimi; degistir(k.id, { birim: b, miktar: birimMiktari(b, kisi, gece) }); }}>
                    {BIRIMLER.map((b) => <option key={b} value={b}>{BIRIM_ETIKETI[b]}</option>)}
                  </select>
                </div>
                <div className="ht-alan">
                  <label htmlFor={`${kimlikK}-miktar`}>{M.kalem.miktar}</label>
                  <input id={`${kimlikK}-miktar`} type="number" inputMode="numeric" min={1} max={KALEM_MIKTAR_AZAMI} step={1} value={k.birim === 'SABIT' ? '1' : k.miktar} disabled={k.birim === 'SABIT'} aria-invalid={d?.hata === 'MIKTAR' ? 'true' : undefined} data-testid={`ht-kalem-miktar-${i}`} onChange={(e) => degistir(k.id, { miktar: e.target.value })} />
                </div>
                <div className="ht-alan ht-kalem-kapsam">
                  <label htmlFor={`${kimlikK}-kapsam`}>{M.kalem.kapsam}</label>
                  <select
                    id={`${kimlikK}-kapsam`}
                    value={kapsamDeger}
                    aria-invalid={d?.hata === 'SECENEK' ? 'true' : undefined}
                    data-testid={`ht-kalem-kapsam-${i}`}
                    onChange={(e) => (e.target.value === 'TUM' ? degistir(k.id, { kapsam: 'TUM', secenek: null }) : degistir(k.id, { kapsam: 'SECENEK', secenek: e.target.value }))}
                  >
                    <option value="TUM">{M.kalem.kapsamTum}</option>
                    {k.kapsam === 'SECENEK' && !secenekVar && k.secenek !== null ? <option value={k.secenek}>{M.kalem.kapsamYok}</option> : null}
                    {secenekler.map((s) => <option key={s.anahtar} value={s.anahtar}>{M.kalem.kapsamYalniz(s.ad)}</option>)}
                  </select>
                </div>
                <div className="ht-alan">
                  <label htmlFor={`${kimlikK}-kdv`}>{M.kalem.kdv}</label>
                  <input id={`${kimlikK}-kdv`} value={k.kdvNotu} maxLength={60} placeholder={M.kalem.kdvIpucu} autoComplete="off" data-testid={`ht-kalem-kdv-${i}`} onChange={(e) => degistir(k.id, { kdvNotu: e.target.value })} />
                </div>
              </div>
              <div className="ht-kalem-alt">
                {d !== undefined && d.hata !== null ? <span className="ht-hata-yazi" role="alert" data-testid={`ht-kalem-hata-${i}`}>{M.kalem.hata[d.hata]}</span> : null}
                {d !== undefined && d.hata === null && d.toplamMinor !== null && hedefPara !== null ? (
                  <span className="ht-kalem-toplam" data-testid={`ht-kalem-toplam-${i}`}>{M.kalem.toplam(`${kalemDayanagi(k, d)} = ${paraYaz(d.toplamMinor, hedefPara)}`)}</span>
                ) : null}
                {d !== undefined && d.hata === null && d.kendiToplamMinor !== null && hedefPara === null ? (
                  <span className="ht-kalem-toplam" data-testid={`ht-kalem-toplam-${i}`}>{M.kalem.toplam(`${kalemDayanagi(k, d)} = ${paraYaz(d.kendiToplamMinor, k.paraBirimi)}`)}</span>
                ) : null}
                {k.paraBirimi === 'AED' && hedefPara === 'USD' ? <span className="ht-ipucu">{M.kalem.aedNotu}</span> : null}
                <button type="button" className="ht-btn" onClick={() => kaldir(k.id)} aria-label={M.kalem.kaldir(i + 1)} data-testid={`ht-kalem-kaldir-${i}`}><Ikon ad="kapat" kucuk />{M.kalem.kaldirEtiket}</button>
              </div>
            </fieldset>
          );
        })}
      </div>
      <div className="ht-ekle-satir">
        <button type="button" className="ht-btn" disabled={devreDisi || kalemler.length >= KALEM_AZAMI} onClick={ekle} data-testid="ht-kalem-ekle"><Ikon ad="arti" kucuk />{M.kalem.ekle}</button>
        {kalemler.length >= KALEM_AZAMI ? <span className="ht-hata-yazi" role="alert" data-testid="ht-kalem-limit">{M.kalem.limit(KALEM_AZAMI)}</span> : null}
      </div>
    </section>
  );
}
